import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  Drawer
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

const ShoppingCart = ({ 
  isOpen, 
  onClose, 
  cartItems = [], 
  onUpdateQuantity, 
  onRemoveItem, 
  onCheckout 
}) => {
  const calculateTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 400,
          boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
          borderRadius: '8px 0 0 8px'
        }
      }}
    >
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        backgroundColor: '#ffffff'
      }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid #e5e5e5'
        }}>
          <Typography sx={{
            fontSize: '18px',
            fontFamily: 'Poppins',
            fontWeight: 600,
            color: '#1a1a1a'
          }}>
            Shopping Cart ({getTotalItems()})
          </Typography>
          <IconButton 
            onClick={onClose}
            sx={{ 
              color: '#666',
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Cart Items */}
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto',
          p: 2
        }}>
          {cartItems.length === 0 ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '200px',
              textAlign: 'center'
            }}>
              <Typography sx={{
                fontSize: '16px',
                fontFamily: 'Poppins',
                color: '#666',
                mb: 1
              }}>
                Your cart is empty
              </Typography>
              <Typography sx={{
                fontSize: '14px',
                color: '#999'
              }}>
                Add items to get started
              </Typography>
            </Box>
          ) : (
            cartItems.map((item, index) => (
              <Box key={index} sx={{
                display: 'flex',
                mb: 3,
                p: 2,
                backgroundColor: '#f9f9f9',
                borderRadius: '8px',
                border: '1px solid #e5e5e5'
              }}>
                {/* Product Image */}
                <Box sx={{
                  width: '60px',
                  height: '70px',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  border: '1px solid #e0e0e0',
                  mr: 2
                }}>
                  <img 
                    src={item.image}
                    alt={item.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Box>

                {/* Product Details */}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography sx={{
                      fontSize: '14px',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      color: '#1a1a1a',
                      lineHeight: '18px'
                    }}>
                      {item.name}
                    </Typography>
                    <IconButton 
                      size="small"
                      onClick={() => onRemoveItem(index)}
                      sx={{ 
                        color: '#999',
                        ml: 1,
                        '&:hover': { color: '#e53e3e' }
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>

                  {/* Price */}
                  <Typography sx={{
                    fontSize: '13px',
                    color: '#666',
                    mb: 1
                  }}>
                    ${item.price.toFixed(2)} each
                  </Typography>

                  {/* Quantity Controls */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#fff',
                      borderRadius: '20px',
                      border: '1px solid #e0e0e0',
                      height: '32px'
                    }}>
                      <IconButton 
                        size="small"
                        onClick={() => onUpdateQuantity(index, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                        sx={{ 
                          color: item.quantity <= 1 ? '#ccc' : '#333',
                          width: '28px',
                          height: '28px'
                        }}
                      >
                        <RemoveIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                      
                      <Typography sx={{
                        minWidth: '30px',
                        textAlign: 'center',
                        fontSize: '14px',
                        fontWeight: 500
                      }}>
                        {item.quantity}
                      </Typography>
                      
                      <IconButton 
                        size="small"
                        onClick={() => onUpdateQuantity(index, item.quantity + 1)}
                        sx={{ 
                          color: '#333',
                          width: '28px',
                          height: '28px'
                        }}
                      >
                        <AddIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>

                    <Typography sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#5c725c',
                      ml: 1
                    }}>
                      ${(item.price * item.quantity).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>

        {/* Footer */}
        {cartItems.length > 0 && (
          <Box sx={{
            borderTop: '1px solid #e5e5e5',
            p: 2,
            backgroundColor: '#f9f9f9'
          }}>
            {/* Total */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}>
              <Typography sx={{
                fontSize: '16px',
                fontFamily: 'Poppins',
                fontWeight: 600,
                color: '#1a1a1a'
              }}>
                {getTotalItems()} Product{getTotalItems() !== 1 ? 's' : ''}
              </Typography>
              <Typography sx={{
                fontSize: '18px',
                fontFamily: 'Poppins',
                fontWeight: 700,
                color: '#5c725c'
              }}>
                ${calculateTotal().toFixed(2)}
              </Typography>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={onCheckout}
                sx={{
                  backgroundColor: '#5c725c',
                  color: 'white',
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  fontSize: '16px',
                  height: '48px',
                  borderRadius: '24px',
                  '&:hover': { backgroundColor: '#3c4a3c' }
                }}
              >
                Checkout
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={onClose}
                sx={{
                  borderColor: '#5c725c',
                  color: '#5c725c',
                  fontFamily: 'Poppins',
                  fontWeight: 500,
                  fontSize: '14px',
                  height: '40px',
                  borderRadius: '20px',
                  '&:hover': { 
                    borderColor: '#3c4a3c',
                    backgroundColor: '#5c725c08'
                  }
                }}
              >
                Continue Shopping
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default ShoppingCart;
